import React from 'react';
import {
  DndContext,
  PointerSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  closestCenter,
  closestCorners,
  getFirstCollision
} from '@dnd-kit/core';

// 创建Context来共享高亮状态和目标侧边栏状态
const TaskDisplayContext = React.createContext<{
  recentlyAssociatedTask: string | null;
  showGoalSidebar: boolean;
}>({
  recentlyAssociatedTask: null,
  showGoalSidebar: false
});

export const useTaskDisplay = () => React.useContext(TaskDisplayContext);
export const useHighlight = () => React.useContext(TaskDisplayContext);
import type {
  DragEndEvent,
  DragOverEvent,
  DragStartEvent
} from '@dnd-kit/core';
import { TaskCard } from '../TaskCard/TaskCard';
import { useTaskStore } from '../../../../stores/taskStore';
import { useGoalStore } from '../../../../stores/goalStore';
import { toast } from '../../../../stores/toastStore';
import type { Task, TaskStatus, TaskPriority } from '../../../../types';

interface DragAndDropProviderProps {
  children: React.ReactNode;
  showGoalSidebar: boolean;
}

export const DragAndDropProvider: React.FC<DragAndDropProviderProps> = ({ children, showGoalSidebar }) => {
  const { tasks, taskTypes, updateTask, reorderTasks, isCompactView, loadTasks } = useTaskStore();
  const { goals } = useGoalStore();
  const [activeTask, setActiveTask] = React.useState<Task | null>(null);
  const [activeElement, setActiveElement] = React.useState<HTMLElement | null>(null);
  const [recentlyAssociatedTask, setRecentlyAssociatedTask] = React.useState<string | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 0, // 零距离激活，完全跟随鼠标
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 0, // 零延迟，立即响应
        tolerance: 0,
      },
    })
  );

  // 拖拽开始
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const task = tasks.find(t => t.id === active.id);
    setActiveTask(task || null);
    
    // 获取原始元素用于复制样式
    const activeElement = document.querySelector(`[data-task-id="${active.id}"]`) as HTMLElement;
    setActiveElement(activeElement);
    
  };

  // 拖拽悬停
  const handleDragOver = (event: DragOverEvent) => {
    // 可以在这里添加实时预览逻辑
  };

  // 拖拽结束
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);
    setActiveElement(null);


    if (!over) {
      return;
    }

    const taskId = active.id as string;
    const task = tasks.find(t => t.id === taskId);
    if (!task) {
      return;
    }

    // 处理sortable排序（同优先级内排序）
    if (over.id === active.id) return; // 拖拽到自己位置，不需要处理

    // 检查是否是拖拽到另一个任务位置
    const overTask = tasks.find(t => t.id === over.id);
    if (overTask) {
      // 如果是不同状态的任务，执行状态切换
      if (overTask.status !== task.status) {
        const newStatus = overTask.status;
        const finalPriority = task.priority; // 保持原优先级
        
        // 计算新的排序值
        const sameStatusTasks = tasks.filter(t => 
          t.status === newStatus && 
          t.priority === finalPriority &&
          t.id !== taskId
        );
        const newOrder = sameStatusTasks.length + 1;

        try {
          // 更新任务状态
          await updateTask(taskId, {
            status: newStatus,
            priority: finalPriority,
            order: newOrder,
            // 如果任务变为完成状态，记录完成时间
            ...(newStatus === 'DONE' && task.status !== 'DONE' ? {
              taskDate: new Date().toISOString()
            } : {})
          });

          // 显示状态变更通知
          const statusLabels = {
            'TODO': '待办',
            'IN_PROGRESS': '进行中',
            'DONE': '已完成'
          };


          return;
        } catch (error) {
          console.error('更新任务状态失败:', error);
          toast.error('状态更新失败', '任务状态更新时发生错误，请重试');
          return;
        }
      }
      
      // 同状态任务的处理逻辑
      if (overTask.status === task.status) {
        // 待办任务：允许所有优先级之间拖拽排序，但保持各自的优先级
        if (task.status === 'TODO') {
          // 获取同状态的所有任务，按 order 降序排序（和显示顺序一致）
          const sameStatusTasks = tasks
            .filter(t => t.status === task.status)
            .sort((a, b) => b.order - a.order);

          const oldIndex = sameStatusTasks.findIndex(t => t.id === taskId);
          const newIndex = sameStatusTasks.findIndex(t => t.id === over.id);

          if (oldIndex === newIndex) return;

          // 重新计算所有任务的order值
          const reorderedTasks = [...sameStatusTasks];
          const [removed] = reorderedTasks.splice(oldIndex, 1);
          reorderedTasks.splice(newIndex, 0, removed);

          // 批量更新order值 - 保持各自的优先级不变
          // 因为待办任务按order降序排列（大的在前），所以第一个任务应该有最大的order值
          const updates = reorderedTasks.map((t, index) => ({
            taskId: t.id,
            newStatus: task.status,
            newPriority: t.priority, // 保持各自的优先级不变
            newOrder: reorderedTasks.length - index  // 第一个任务order最大
          }));

          // 检查当前是否为优先级模式，如果是则不允许拖拽排序
          const currentSortMode = localStorage.getItem('todoSortMode');
          if (currentSortMode === 'priority') {
            return;
          }

          try {
            // 使用store的reorderTasks方法
            await reorderTasks(updates);
            return;
          } catch (error) {
            console.error('重新排序任务失败:', error);
            return;
          }
        } else {
          // 进行中和完成任务：纯排序，不考虑优先级
          const sameStatusTasks = tasks
            .filter(t => t.status === task.status)
            .sort((a, b) => a.order - b.order);

          const oldIndex = sameStatusTasks.findIndex(t => t.id === taskId);
          const newIndex = sameStatusTasks.findIndex(t => t.id === over.id);

          if (oldIndex === newIndex) return;

          // 重新计算所有任务的order值
          const reorderedTasks = [...sameStatusTasks];
          const [removed] = reorderedTasks.splice(oldIndex, 1);
          reorderedTasks.splice(newIndex, 0, removed);

          // 批量更新order值 - 保持原优先级不变
          const updates = reorderedTasks.map((t, index) => ({
            taskId: t.id,
            newStatus: task.status,
            newPriority: t.priority, // 保持各自的优先级不变
            newOrder: index + 1
          }));

          try {
            // 使用store的reorderTasks方法
            await reorderTasks(updates);
            return;
          } catch (error) {
            console.error('重新排序任务失败:', error);
            return;
          }
        }
      }
    }

    // 处理拖拽到目标
    const overData = over.data.current;
    if (overData && overData.type === 'goal') {
      try {
        // 如果拖拽到"独立任务"，清除目标关联
        const goalId = overData.goalId === 'unlinked' ? null : overData.goalId;

        await updateTask(taskId, {
          goalId: goalId
        });

        // 重新加载任务数据以确保关联信息同步
        await loadTasks();

        // 设置高亮效果
        setRecentlyAssociatedTask(taskId);
        setTimeout(() => {
          setRecentlyAssociatedTask(null);
        }, 2000); // 2秒后移除高亮
        return;
      } catch (error) {
        console.error('关联任务到目标失败:', error);
        toast.error('关联失败', '任务关联到目标时发生错误，请重试');
        return;
      }
    }

    // 处理跨状态/优先级拖拽
    if (!overData || overData.type !== 'zone') {
      return;
    }

    const newStatus = overData.status as TaskStatus;
    const newPriority = overData.priority as TaskPriority | undefined;

    // 确定最终的优先级：只有拖拽到具体优先级区域才改变优先级，否则保持原优先级
    const finalPriority = newPriority ? newPriority : task.priority;

    // 如果状态和优先级都没变化，则不更新
    if (task.status === newStatus && task.priority === finalPriority) {
      return;
    }

    // 计算新的排序值
    const sameStatusTasks = tasks.filter(t => 
      t.status === newStatus && 
      t.priority === finalPriority &&
      t.id !== taskId
    );
    const newOrder = sameStatusTasks.length + 1;

    try {
      // 更新任务状态
      await updateTask(taskId, {
        status: newStatus,
        priority: finalPriority,
        order: newOrder,
        // 如果任务变为完成状态，记录完成时间
        ...(newStatus === 'DONE' && task.status !== 'DONE' ? {
          taskDate: new Date().toISOString()
        } : {})
      });

      // 显示状态变更通知
      const statusLabels = {
        'TODO': '待办',
        'IN_PROGRESS': '进行中',
        'DONE': '已完成'
      };

      // 任务状态更新成功，但不显示提示

    } catch (error) {
      console.error('更新任务状态失败:', error);
      toast.error('状态更新失败', '任务状态更新时发生错误，请重试');
    }
  };

  // 获取任务类型
  const getTaskType = (taskTypeId: string) => {
    return taskTypes.find(type => type.id === taskTypeId);
  };

  // 自定义碰撞检测：优先考虑指针的水平位置
  const customCollisionDetection = (args: any) => {
    // 首先使用 closestCorners 获取所有碰撞
    const closestCornersCollisions = closestCorners(args);

    if (closestCornersCollisions.length > 0) {
      // 如果有碰撞，优先选择指针x坐标在其范围内的zone
      const pointerX = args.pointerCoordinates?.x;

      if (pointerX) {
        const collisionWithPointer = closestCornersCollisions.find((collision: any) => {
          const rect = args.droppableRects.get(collision.id);
          if (rect) {
            return pointerX >= rect.left && pointerX <= rect.right;
          }
          return false;
        });

        if (collisionWithPointer) {
          return [collisionWithPointer];
        }
      }

      // 如果没有找到指针所在的zone，返回最近的第一个结果
      return [closestCornersCollisions[0]];
    }

    return [];
  };

  return (
    <TaskDisplayContext.Provider value={{ recentlyAssociatedTask, showGoalSidebar }}>
      <DndContext
        sensors={sensors}
        collisionDetection={customCollisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        {children}

        {/* 拖拽预览层 */}
        <DragOverlay>
          {activeTask && activeElement ? (
            <div
              className="drag-preview"
              style={{
                width: activeElement.offsetWidth,
                height: activeElement.offsetHeight,
                transformOrigin: 'center',
                overflow: 'hidden',
                transition: 'none',
                opacity: 0.95,
                borderRadius: '8px',
                border: '1px solid rgba(59, 130, 246, 0.3)',
              }}
            >
              <TaskCard
                task={activeTask}
                taskType={getTaskType(activeTask.taskTypeId)}
                isCompact={isCompactView}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </TaskDisplayContext.Provider>
  );
};